import { api } from '../../services/api';
import type { DeviceHandle, DeviceDirection } from './types';
import { rawDeviceToHandle } from './mapping';

/**
 * Fetch DeviceHandle[] for every device a person currently has access to,
 * derived from their effective-access-zones.
 *
 * Includes the zone `entry_type` as `direction` on each handle so callers
 * can apply per-direction filtering without re-fetching.
 *
 * Silent failures: device fetches that throw are skipped (Promise.allSettled).
 */
export async function fetchPersonDeviceHandles(
  customerId: string,
  personId: string,
): Promise<DeviceHandle[]> {
  try {
    const zonesResp = await api.get(
      `/customers/${customerId}/people/${personId}/effective-access-zones`,
    );
    const raw = zonesResp.data;
    const zones: any[] = Array.isArray(raw?.zones) ? raw.zones
      : Array.isArray(raw?.data) ? raw.data
      : Array.isArray(raw) ? raw
      : [];

    const deviceIdSet = new Set<string>();
    const directionMap = new Map<string, DeviceDirection>();

    zones.forEach((zone: any) => {
      zone.devices?.forEach((d: any) => {
        deviceIdSet.add(d.id);
        if (zone.entry_type && !directionMap.has(d.id)) {
          directionMap.set(d.id, zone.entry_type as DeviceDirection);
        }
      });
    });

    if (deviceIdSet.size === 0) return [];

    const results = await Promise.allSettled(
      Array.from(deviceIdSet).map((deviceId) =>
        api.get(`/iot-devices/${deviceId}`).then((r) =>
          rawDeviceToHandle(r.data, directionMap.get(deviceId)),
        ),
      ),
    );

    return results
      .filter((r): r is PromiseFulfilledResult<DeviceHandle> => r.status === 'fulfilled')
      .map((r) => r.value);
  } catch {
    return [];
  }
}
